import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiInstance } from '../../api/apiInstance';
import { RootState } from '../../store/store';
import { NotificationSectionsType } from './constants';
import { OrganizationType } from 'src/utils/enums/organization-type.enum';

interface updateCellPhoneArgs {
  phone: string;
  isSmsAllowed: boolean;
  countryCode: string;
}

export const getProfileInfo = createAsyncThunk('notifications/getProfileInfo', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/profile');

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const updateProfile = createAsyncThunk(
  'notifications/updateNotification',
  async (role: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const preferences =
        role === 'admin'
          ? state.notifications.currentPreferences
          : { personalActivity: state.notifications.currentPreferences.personalActivity };

      const response = await apiInstance.patch('/profile/notifications', JSON.stringify(preferences));

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const updateCellPhone = createAsyncThunk(
  'notifications/updateCellPhone',
  async (args: updateCellPhoneArgs, { rejectWithValue }) => {
    const { phone, isSmsAllowed, countryCode } = args;
    try {
      const response = await apiInstance.patch('/profile', { cellPhoneNumber: phone, isSmsAllowed, countryCode });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getNotificationSections = (type: string, role: string) => {
  const notificationSections: NotificationSectionsType[] = [
    {
      title: 'Personal Activity',
      type: 'personalActivity',
      notifications: [
        { label: 'Job Submission', subText: 'Alert when you submit job', key: 'jobSubmission' },
        { label: 'Job On Hold', subText: 'Alert when your job is on hold', key: 'jobOnHold', disabled: true },
        {
          label: 'Job Completion',
          subText: 'Alert when your job is complete',
          key: 'jobCompletion',
          disabled: true,
          nested: [
            {
              label: 'Attach Certificate',
              subText: 'Attach Certificate of Service to email?',
              key: 'attachCertificate',
            },
            ...(type !== OrganizationType.CHAPTER_13_TRUSTEE
              ? [
                  {
                    label: 'Attach Invoice',
                    subText: 'Attach Invoice to email?',
                    key: 'attachInvoice',
                  },
                ]
              : []),
            ,
          ],
        },
        ...(type !== OrganizationType.CHAPTER_13_TRUSTEE && role !== 'admin'
          ? [
              {
                label: 'Payment Declined',
                subText: 'Alert when payment declines',
                key: 'paymentDeclined',
                disabled: true,
              },
            ]
          : []),
      ],
    },
  ];

  const orgPreferences: NotificationSectionsType = {
    title: 'Organization Activity',
    type: 'organizationActivity',
    notifications: [
      { label: "Teammate's Job Submission", subText: 'Alert when teammate submits job', key: 'teammateJobSubmission' },
      { label: "Teammate's Job On Hold", subText: "Alert when teammate's job is on hold", key: 'teammateJobOnHold' },
      {
        label: "Teammate's Job Completion",
        subText: "Alert when teammate's job is complete",
        key: 'teammateJobCompletion',
        nested: [
          {
            label: 'Attach Certificate',
            subText: 'Attach Certificate of Service to email?',
            key: 'attachCertificate',
          },
          ...(type !== OrganizationType.CHAPTER_13_TRUSTEE
            ? [
                {
                  label: 'Attach Invoice',
                  subText: 'Attach Invoice to email?',
                  key: 'attachInvoice',
                },
              ]
            : []),
        ],
      },
      ...(type === OrganizationType.CHAPTER_13_TRUSTEE
        ? [
            {
              label: 'Monthly invoice',
              subText: 'Alert when monthly invoice sent',
              key: 'monthlyInvoice',
              disabled: true,
            },
          ]
        : []),
      {
        label: 'Teammate Requests',
        key: 'teammateRequests',
        subText: 'Alert when teammate requests access to organization',
        disabled: true,
      },
      ...(type !== OrganizationType.CHAPTER_13_TRUSTEE
        ? [
            {
              label: 'Payment Declined',
              key: 'teammatePaymentDeclined',
              subText: 'Alert when payment declines',
              disabled: true,
            },
          ]
        : []),
    ],
  };

  const updatedNotificationSections =
    role === 'admin' ? [...notificationSections, orgPreferences] : notificationSections;

  return updatedNotificationSections;
};
